import { Stack, Text, useMantineColorScheme } from '@mantine/core';
import Editor from '@monaco-editor/react';
import { useEffect, useState } from 'react';
import { PagePanel } from '../../common';
import {
  deserializeSchemaToEditorSource,
  serializeEditorSourceToSchema,
} from '../../../lib';
import { StyledEditorWrap, StyledSchemaContentFormLayout } from './styles';
import { registerSchemaEditorMonacoLibs } from './register-schema-editor-monaco-libs';

export type SchemaContentValues = {
  updateTemplateDiscardFields: string[];
  config?: Record<string, unknown>;
  definition: Record<string, unknown>;
};

type SchemaContentFormProps = {
  formId: string;
  initialValues: SchemaContentValues;
  onSubmit: (payload: SchemaContentValues) => Promise<void>;
  readOnly?: boolean;
};

export const SchemaContentForm = ({
  formId,
  initialValues,
  onSubmit,
  readOnly = false,
}: SchemaContentFormProps) => {
  const [source, setSource] = useState('');
  const { colorScheme } = useMantineColorScheme();

  useEffect(() => {
    const editorSource = deserializeSchemaToEditorSource(initialValues);

    if (editorSource.isErr()) {
      setSource('');
      return;
    }

    setSource(editorSource.value);
  }, [initialValues]);

  const handleSubmit = async () => {
    if (readOnly) {
      return;
    }

    const serialized = serializeEditorSourceToSchema(source);

    if (serialized.isErr()) {
      return;
    }

    await onSubmit(serialized.value);
  };

  return (
    <form
      id={formId}
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
    >
      <StyledSchemaContentFormLayout>
        <PagePanel padding={0} overflow="hidden">
          <Stack spacing={0}>
            <Text px={20} py={16} size="sm" color="dimmed">
              {readOnly
                ? 'Viewing a historical schema version.'
                : 'Edit the deserialized runtime schema. Zod helpers are available as z.'}
            </Text>

            <StyledEditorWrap>
              <Editor
                theme={colorScheme === 'dark' ? 'vs-dark' : 'light'}
                beforeMount={registerSchemaEditorMonacoLibs}
                defaultLanguage="typescript"
                height="70vh"
                value={source}
                onChange={(value) => setSource(value ?? '')}
                options={{
                  automaticLayout: true,
                  fontSize: 14,
                  minimap: { enabled: false },
                  readOnly,
                  scrollBeyondLastLine: false,
                  tabSize: 2,
                }}
              />
            </StyledEditorWrap>
          </Stack>
        </PagePanel>
      </StyledSchemaContentFormLayout>
    </form>
  );
};
